/**
 * Snackbar store - Global snackbar notifications.
 * Replaces SnackbarContext.
 */

import { create } from 'zustand';
import { logger } from '../../helpers/logger.helper';
import { useSoundStore } from './soundStore';

export type SnackbarVariant = 'success' | 'error' | 'info' | 'warning';

export interface SnackbarAction {
  label: string;
  onPress: () => void;
}

export interface SnackbarOptions {
  /** Override the default auto-hide duration (3000ms). Useful for actionable snackbars that need reading time. */
  durationMs?: number;
  /** Optional CTA rendered in the snackbar. Pressing it runs `onPress` and dismisses the snackbar. */
  action?: SnackbarAction;
}

export interface SnackbarState {
  message: string;
  variant: SnackbarVariant;
  /** Optional. When omitted, banner derives from variant (Error, Information, Success, Warning). */
  title?: string;
  durationMs?: number;
  action?: SnackbarAction;
}

interface SnackbarStoreState {
  snackbar: SnackbarState | null;
}

interface SnackbarStoreActions {
  showSnackbar: (
    message: string,
    variant?: SnackbarVariant,
    title?: string,
    options?: SnackbarOptions,
  ) => void;
  clearSnackbar: () => void;
}

const DEFAULT_TITLES: Record<SnackbarVariant, string> = {
  error: 'Error',
  info: 'Information',
  success: 'Success',
  warning: 'Warning',
};

export const getDefaultSnackbarTitle = (variant: SnackbarVariant): string =>
  DEFAULT_TITLES[variant];

export const useSnackbarStore = create<SnackbarStoreState & SnackbarStoreActions>((set) => ({
  snackbar: null,

  showSnackbar: (
    message: string,
    variant: SnackbarVariant = 'error',
    title?: string,
    options?: SnackbarOptions,
  ) => {
    if (variant === 'error') {
      logger.error('[Snackbar]', message);
    } else {
      logger.debug('[Snackbar]', message);
    }
    useSoundStore.getState().playForSnackbarVariant(variant);
    set({
      snackbar: {
        message,
        variant,
        title,
        durationMs: options?.durationMs,
        action: options?.action,
      },
    });
  },

  clearSnackbar: () => set({ snackbar: null }),
}));
